import { useCallback, useMemo, useState } from 'react';
import { useSubscriptionState } from './useSubscriptionState';
import { useRevenueCat } from './useRevenueCat';
import { useAuth } from './useAuth';
import {
  persistPreference,
  readPersistedPreference,
} from '../lib/app-preferences-storage';
import {
  createLapseAckStore,
  shouldShowLapseNotice,
} from '../lib/subscription-journey/lapse-notice';

const lapseAckStore = createLapseAckStore({
  // SecureStore on native, localStorage on web (per device).
  read: readPersistedPreference,
  write: persistPreference,
});

/**
 * J: "You're on Free now." once per expiry episode. The global coordinator
 * (native: next to NativeFeedbackCoordinator; web: app shell) renders the
 * notice when `episode` is non-null and calls `acknowledge()` on ANY dismissal
 * (Explore plans, Continue with Free, close, swipe).
 *
 * Suppressed while RevenueCat is busy (purchase/restore/sync in flight) or the
 * subscription state is still loading.
 */
export function useLapseNotice() {
  const { user } = useAuth();
  const { snapshot, isLoading } = useSubscriptionState();
  const { isBusy } = useRevenueCat();
  const accountId = user?.id ?? null;
  // Bumps after acknowledge so the stored key is re-read.
  const [ackVersion, setAckVersion] = useState(0);

  const acknowledgedKey = useMemo(
    () => (accountId ? lapseAckStore.get(accountId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId, ackVersion],
  );

  const episode = accountId
    ? shouldShowLapseNotice({
        snapshot,
        acknowledgedKey,
        isSyncing: isBusy || isLoading,
      })
    : null;

  const acknowledge = useCallback(() => {
    if (!accountId || !episode) return;
    lapseAckStore.acknowledge(accountId, episode.key);
    setAckVersion((value) => value + 1);
  }, [accountId, episode]);

  return { episode, acknowledge };
}
